export const HEX_COLOR_REGEX = /^#([0-9A-F]{3}){1,2}$/i;

export function getContrastTextColor(hexColor: string) {
  const hexColorRegex = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;
  if (!hexColorRegex.test(hexColor)) {
    console.warn("The input provided was not a hex color value.");
    return "#000000";
  }

  let r: number;
  let g: number;
  let b: number;

  // Convert hex color to RGB
  if (hexColor.length === 4) {
    // Handle 3-digit hex colors
    r = parseInt(`${hexColor[1]}${hexColor[1]}`, 16);
    g = parseInt(`${hexColor[2]}${hexColor[2]}`, 16);
    b = parseInt(`${hexColor[3]}${hexColor[3]}`, 16);
  } else {
    r = parseInt(hexColor.slice(1, 3), 16);
    g = parseInt(hexColor.slice(3, 5), 16);
    b = parseInt(hexColor.slice(5, 7), 16);
  }

  // Calculate relative luminance using the formula for sRGB
  const luminance = 0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255);

  // Determine the best contrasting text color (black or white)
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

export const isWhite = (color?: string) => !!color && ["#ffffff", "#fff"].includes(color.toLowerCase());

// Background colors for the phone gradient background.
const PHONE_GRADIENT_BG = "rgb(23 23 23)";
const PHONE_GRADIENT_BG_LIGHT = "rgb(250 250 250)";

/**
 * Parse hex/rgb string to r,g,b (0-255).
 * @param color - The color to parse.
 * @returns The parsed color as an array of [r, g, b].
 */
const parseColor = (color: string): [number, number, number] => {
  const hex = color.replace(/^#/, "");
  if (hex.length === 6 || hex.length === 8) {
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    return [r, g, b];
  }
  const rgb = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return [23, 23, 23];
};

/**
 * Mix two color strings; t=0 => c1, t=1 => c2. Returns rgba() string.
 * @param c1 - The first color.
 * @param c2 - The second color.
 * @param t - The mix ratio (0-1).
 * @param alpha - The alpha value (0-1).
 * @returns The mixed color as an rgba() string.
 */
const mixColors = (c1: string, c2: string, t: number, alpha = 1): string => {
  const [r1, g1, b1] = parseColor(c1);
  const [r2, g2, b2] = parseColor(c2);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `rgba(${r},${g},${b},${alpha})`;
};

/**
 * Build CSS background to match mobile BackgroundGradient (Skia):
 * - One color / secondary black: primary with opacity fade (1 → 0.7) then fade to transparent.
 * - Two colors: primary → mix → secondary then fade to transparent.
 * - Mask fade: gradient visible at top, fades to PHONE_GRADIENT_BG toward bottom.
 * @param primaryColor - The primary color.
 * @param secondaryColor - The secondary color.
 * @returns The CSS background to match mobile BackgroundGradient (Skia).
 */
export const getPhoneGradientBackground = (primaryColor: string, secondaryColor?: string) => {
  const useTwoColors = secondaryColor && secondaryColor !== primaryColor;

  let colorStops: string;
  if (useTwoColors) {
    const c0 = mixColors(primaryColor, secondaryColor, 0, 0.85);
    const c1 = mixColors(primaryColor, secondaryColor, 0.45, 1);
    const c2 = mixColors(primaryColor, secondaryColor, 0.75, 1);
    const c3 = mixColors(primaryColor, secondaryColor, 1, 1);
    colorStops = `${c0} 0%, ${c1} 30%, ${c2} 50%, ${c3} 82%, ${c3} 100%`;
  } else {
    const [r, g, b] = parseColor(primaryColor);
    const c0 = `rgba(${r},${g},${b},1)`;
    const c1 = `rgba(${r},${g},${b},0.95)`;
    const c2 = `rgba(${r},${g},${b},0.9)`;
    const c3 = `rgba(${r},${g},${b},0.85)`;
    const c4 = `rgba(${r},${g},${b},0.7)`;
    colorStops = `${c0} 0%, ${c1} 30%, ${c2} 70%, ${c3} 80%, ${c4} 100%`;
  }

  // Mobile: LinearGradient start (0,0) end (0.8*w, 0.4*h) ≈ top-left to bottom-right (~120deg)
  // Mask: fades from opaque (top) to transparent (bottom) so gradient blurs to background; kept shorter so gradient is less tall
  const gradientLayer = `linear-gradient(120deg, ${colorStops})`;
  const maskFadeLayer = `linear-gradient(to bottom, transparent 0%, transparent 12%, rgba(23,23,23,0.5) 38%, ${PHONE_GRADIENT_BG} 62%, ${PHONE_GRADIENT_BG} 100%)`;

  return {
    backgroundColor: PHONE_GRADIENT_BG,
    backgroundImage: `${maskFadeLayer}, ${gradientLayer}`,
  };
};

/**
 * Light-mode phone: primary (with transparency) blending to white, mask fades to light background.
 * @param primaryColor - The primary color.
 * @returns The CSS background to match mobile BackgroundGradient (Skia) in light mode.
 */
export const getPhoneGradientBackgroundLight = (primaryColor: string) => {
  const white = "rgb(255,255,255)";
  const c0 = mixColors(primaryColor, white, 0, 0.75);
  const c1 = mixColors(primaryColor, white, 0.25, 0.9);
  const c2 = mixColors(primaryColor, white, 0.55, 1);
  const c3 = mixColors(primaryColor, white, 0.85, 1);
  const c4 = mixColors(primaryColor, white, 1, 1);
  const colorStops = `${c0} 0%, ${c1} 25%, ${c2} 55%, ${c3} 80%, ${c4} 100%`;

  const gradientLayer = `linear-gradient(120deg, ${colorStops})`;
  const maskFadeLayer = `linear-gradient(to bottom, transparent 0%, transparent 12%, rgba(250,250,250,0.5) 38%, ${PHONE_GRADIENT_BG_LIGHT} 62%, ${PHONE_GRADIENT_BG_LIGHT} 100%)`;

  return {
    backgroundColor: PHONE_GRADIENT_BG_LIGHT,
    backgroundImage: `${maskFadeLayer}, ${gradientLayer}`,
  };
};
